import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import type { CrmFunilTipo } from '@/constants/crmFunil';

export type ClienteCadastroOption = {
  id: string;
  tipo: 'fisica' | 'juridica';
  nome?: string | null;
  razaoSocial?: string | null;
  cpf?: string | null;
  cnpj?: string | null;
  email?: string | null;
  telefone?: string | null;
  label: string;
};

type ClienteExistenteComboboxProps = {
  tipo: 'fisica' | 'juridica';
  value: string | null;
  onSelect: (cliente: ClienteCadastroOption | null) => void;
  crmFunil?: CrmFunilTipo;
  disabled?: boolean;
};

export default function ClienteExistenteCombobox({
  tipo,
  value,
  onSelect,
  crmFunil,
  disabled = false,
}: ClienteExistenteComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState<ClienteCadastroOption[]>([]);
  const [selectedLabel, setSelectedLabel] = useState('');

  const loadClientes = useCallback(
    async (q: string) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ tipo });
        if (q.trim()) params.set('q', q.trim());
        if (crmFunil) params.set('crm_funil', crmFunil);

        const res = await fetchWithAuth(`/api/leads/cadastro/clientes?${params.toString()}`);
        if (!res.ok) {
          setClientes([]);
          return;
        }
        const data = await res.json();
        setClientes(Array.isArray(data.clientes) ? data.clientes : []);
      } catch {
        setClientes([]);
      } finally {
        setLoading(false);
      }
    },
    [tipo, crmFunil]
  );

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      void loadClientes(search);
    }, 250);
    return () => clearTimeout(timer);
  }, [open, search, loadClientes]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }
    const found = clientes.find((c) => c.id === value);
    if (found) {
      setSelectedLabel(found.label);
    }
  }, [value, clientes]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedLabel || value ? selectedLabel || 'Cliente selecionado' : 'Buscar cliente...'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite o nome do cliente..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
            ) : (
              <>
                <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                <CommandGroup>
                  {clientes.map((cliente) => (
                    <CommandItem
                      key={cliente.id}
                      value={cliente.id}
                      onSelect={() => {
                        onSelect(cliente);
                        setSelectedLabel(cliente.label);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === cliente.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate font-medium">{cliente.label}</span>
                        {cliente.email && (
                          <span className="text-xs text-muted-foreground truncate">{cliente.email}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
